import { Currency, getCurrencySymbol } from '@/constants/Currency';
import { DEFAULT_DECIMAL_PLACES } from '@/constants/NumberConstants';
import { displayStaticDatum, preciseValue } from '@/filters';
import type { RiskDashboardFieldConfig } from '@/types/RiskDashboardFieldConfig';
import { UnitConstants as ReportFieldUnitConstants } from '@/types/admin/reportFields';
import { UnitConstants as DisplayUnitConstants } from '@/types/analytics/ConstituentRisk';

// Percentage report fields still stored and returned as 0–100, so they are not multiplied by 100.
export const PERCENTAGE_FIELDS_WITHOUT_SCALING = new Set(['Volatility', 'Duration Times Spread (DTS)']);

export interface FormatValueWithFieldConfigOptions {
  currency?: string;
  column?: string;
  displayUnit?: DisplayUnitConstants;
  isStaticValue?: boolean;
}

function isCurrencyDisplayUnit(displayUnit?: DisplayUnitConstants): boolean {
  return displayUnit === DisplayUnitConstants.CURRENCY;
}

function isPercentDisplayUnit(displayUnit?: DisplayUnitConstants): boolean {
  return displayUnit === DisplayUnitConstants.PERCENT_VALUE || displayUnit === DisplayUnitConstants.PERCENT_SELECTION;
}

function shouldUseLiteralPercentForCurrencyField(
  config: RiskDashboardFieldConfig,
  displayUnit?: DisplayUnitConstants,
): boolean {
  if (!isPercentDisplayUnit(displayUnit)) {
    return false;
  }

  return config.unitScaling === true;
}

function scalePercentageReportFieldValue(value: number, column?: string): number {
  if (column != null && PERCENTAGE_FIELDS_WITHOUT_SCALING.has(column)) {
    return value;
  }

  return preciseValue(value * 100);
}

function formatNumericValue(
  value: number,
  decimalPlaces: number,
  { percent = false }: { percent?: boolean } = {},
): string {
  return displayStaticDatum(value, {
    numDecimalPoints: decimalPlaces,
    percent,
    useSeparators: true,
  });
}

function formatCurrencyValue(value: number, decimalPlaces: number, currency: string): string {
  const formattedValue = value.toLocaleString(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  return `${getCurrencySymbol(currency as Currency)} ${formattedValue}`;
}

export function resolveCurrencyForFieldConfig(
  config: RiskDashboardFieldConfig,
  rowCurrency: string | null | undefined,
  reportCurrency: Currency,
): Currency {
  if (config.unit === ReportFieldUnitConstants.HOLDING_CCY) {
    if (typeof rowCurrency === 'string' && rowCurrency !== '') {
      return rowCurrency as Currency;
    }

    return reportCurrency;
  }

  if (config.unit === ReportFieldUnitConstants.REPORT_CCY) {
    return reportCurrency;
  }

  return reportCurrency;
}

function formatCurrencyFieldValue(
  value: number,
  decimalPlaces: number,
  config: RiskDashboardFieldConfig,
  options: FormatValueWithFieldConfigOptions,
): string {
  const { currency, displayUnit, isStaticValue = false } = options;

  if (isCurrencyDisplayUnit(displayUnit)) {
    return formatCurrencyValue(value, decimalPlaces, currency ?? Currency.USD);
  }

  if (shouldUseLiteralPercentForCurrencyField(config, displayUnit)) {
    return formatNumericValue(value, decimalPlaces, { percent: true });
  }

  if (isPercentDisplayUnit(displayUnit)) {
    return formatCurrencyValue(value, decimalPlaces, currency ?? Currency.USD);
  }

  if (isStaticValue) {
    return formatCurrencyValue(value, decimalPlaces, currency ?? Currency.USD);
  }

  return formatNumericValue(value, decimalPlaces);
}

export function formatValueWithFieldConfig(
  value: number,
  config: RiskDashboardFieldConfig,
  options: FormatValueWithFieldConfigOptions = {},
): string | null {
  const decimalPlaces = config.decimalPlaces ?? DEFAULT_DECIMAL_PLACES;
  const { column, displayUnit, isStaticValue = false } = options;
  const shouldShowPercent = isPercentDisplayUnit(displayUnit) && !isStaticValue;

  if (config.unit === ReportFieldUnitConstants.TEXT) {
    return String(value);
  }

  if (config.unit === ReportFieldUnitConstants.PERCENTAGE) {
    return formatNumericValue(scalePercentageReportFieldValue(value, column), decimalPlaces, {
      percent: true,
    });
  }

  if (config.unit === ReportFieldUnitConstants.NUMERIC) {
    return formatNumericValue(value, decimalPlaces, { percent: false });
  }

  if (config.unit === ReportFieldUnitConstants.REPORT_CCY || config.unit === ReportFieldUnitConstants.HOLDING_CCY) {
    return formatCurrencyFieldValue(value, decimalPlaces, config, options);
  }

  if (config.unit != null) {
    return null;
  }

  return formatNumericValue(value, decimalPlaces, {
    percent: shouldShowPercent,
  });
}
